import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageForm, { buildInitial, TYPE_LABELS } from "./PageForm";
import PageCsvImport from "./PageCsvImport";

export default function PageEditor() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  // Single-page form vs. bulk CSV import (create mode only).
  const [mode, setMode] = useState("form");

  const [formData, setFormData] = useState(() => buildInitial(type || "grid-guide"));
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [slugError, setSlugError] = useState("");
  const slugRef = useRef(null);

  useEffect(() => {
    if (id) {
      setLoading(true);
      fetch(`${process.env.REACT_APP_BASE_URL}/pages?slug=${id}`)
        .then((res) => res.json())
        .then((data) => {
          // Merge onto type defaults so any missing field stays editable.
          const base = buildInitial(data.type || "grid-guide");
          setFormData({ ...base, ...data });
          setLoading(false);
        })
        .catch((e) => {
          console.error(e);
          setLoading(false);
        });
    }
  }, [id]);

  const pageType = formData.type;

  /* ── submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const slug = (formData.slug || "").trim().toLowerCase().replace(/\s+/g, "-");
    if (!isEdit) {
      if (!slug) {
        setSlugError("Slug is required.");
        setSubmitting(false);
        slugRef.current?.focus();
        return;
      }
      try {
        const checkRes = await fetch(
          `${process.env.REACT_APP_BASE_URL}/pages/check-slug?slug=${encodeURIComponent(slug)}`
        );
        if (!checkRes.ok) throw new Error("check failed");
        const taken = await checkRes.json();
        if (taken) {
          setSlugError("This slug is already taken (or reserved). Choose another.");
          slugRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          slugRef.current?.focus();
          setSubmitting(false);
          return;
        }
        setSlugError("");
      } catch (err) {
        console.error("Error checking slug", err);
        setSlugError("Could not verify slug. Check server connection.");
        setSubmitting(false);
        return;
      }
    }

    const payload = { ...formData, slug, id: slug };
    const url = isEdit
      ? `${process.env.REACT_APP_BASE_URL}/pages/${id}`
      : `${process.env.REACT_APP_BASE_URL}/pages`;
    const method = isEdit ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        alert("Page saved successfully!");
        if (!isEdit) navigate(`/edit-page/${slug}`);
      } else {
        alert("Failed to save page.");
      }
    } catch (error) {
      console.error(error);
      alert("Error saving page.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto h-screen overflow-y-auto pb-24">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">
          {isEdit ? "Edit Page" : "Create Page"}
          <span className="text-[#4d8a82] ml-2">({TYPE_LABELS[pageType] || pageType})</span>
        </h1>

        {/* Single vs. bulk CSV toggle — create mode only */}
        {!isEdit && (
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden self-start">
            <button
              type="button"
              onClick={() => setMode("form")}
              className={`px-4 py-2 text-sm font-semibold ${
                mode === "form" ? "bg-[#4d8a82] text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              Single (Form)
            </button>
            <button
              type="button"
              onClick={() => setMode("csv")}
              className={`px-4 py-2 text-sm font-semibold ${
                mode === "csv" ? "bg-[#4d8a82] text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              Bulk (CSV)
            </button>
          </div>
        )}
      </div>

      {!isEdit && mode === "csv" ? (
        <PageCsvImport type={pageType} />
      ) : (
        <PageForm
          formData={formData}
          setFormData={setFormData}
          slugError={slugError}
          onSlugChange={() => setSlugError("")}
          slugDisabled={isEdit}
          slugRef={slugRef}
          onSubmit={handleSubmit}
          submitLabel={isEdit ? "Save Changes" : "Publish Page"}
          submitting={submitting}
        />
      )}
    </div>
  );
}
